/**
 * Avatar — User avatar with image or initials fallback
 *
 * Variants:
 *   - size: sm(32), md(48), lg(88)
 */
import { type ComponentProps } from 'react'
import { Image } from 'react-native'
import { styled, XStack, Text as TamaguiText, type GetProps } from 'tamagui'

const AvatarFrame = styled(XStack, {
  name: 'Avatar',
  ai: 'center',
  jc: 'center',
  br: '$full',
  overflow: 'hidden',
  bg: '$colorPrimary',

  variants: {
    size: {
      sm: { w: 32, h: 32 },
      md: { w: 48, h: 48 },
      lg: { w: 88, h: 88 },
    },
  } as const,

  defaultVariants: {
    size: 'md',
  },
})

const AvatarInitials = styled(TamaguiText, {
  name: 'AvatarInitials',
  fontFamily: '$body',
  color: '$colorInverse',
  fontWeight: '700',

  variants: {
    size: {
      sm: { fontSize: 12, lh: 12 },
      md: { fontSize: 18, lh: 18 },
      lg: { fontSize: 32, lh: 32 },
    },
  } as const,

  defaultVariants: {
    size: 'md',
  },
})

type AvatarFrameProps = GetProps<typeof AvatarFrame>

interface AvatarProps extends AvatarFrameProps {
  src?: string | null
  initials?: string
}

export const Avatar = AvatarFrame.styleable<AvatarProps>(
  ({ src, initials, size, ...props }, ref) => {
    return (
      <AvatarFrame ref={ref} size={size} {...props}>
        {src ? (
          <Image
            source={{ uri: src }}
            style={{ width: '100%', height: '100%' }}
          />
        ) : (
          <AvatarInitials size={size}>
            {initials || '?'}
          </AvatarInitials>
        )}
      </AvatarFrame>
    )
  },
)
